import React from 'react';
import { Incident } from '../types/incident';

interface ConfirmationProps {
  incident:        Incident;
  onDashboard:     () => void;
  onReportAnother: () => void;
  onStartChat?:    () => void;
}

const SEVERITY_COLOUR: Record<string, string> = {
  CRITICAL: '#dc2626',
  MODERATE: '#ea580c',
  MINOR:    '#16a34a',
};

type StepStatus = 'done' | 'current' | 'pending';
interface PipelineStep {
  label: string;
  sub:   string;
  status: StepStatus;
}

// White check for completed nodes.
function CheckMark() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function Confirmation({ incident, onDashboard, onReportAnother, onStartChat }: ConfirmationProps) {
  const colour = SEVERITY_COLOUR[incident.severity] ?? '#6b7280';

  const d = new Date(incident.occurredAt);
  const occurredAt =
    d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) +
    ' · ' +
    d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();

  // The pipeline IS the page — an ordered sequence, so stepped markers
  // carry real information. Critical/Moderate route through the agent
  // chat (the live "current" node); Minor is logged straight through.
  const isMajor = incident.severity === 'CRITICAL' || incident.severity === 'MODERATE';
  const steps: PipelineStep[] = isMajor
    ? [
        { label: 'Incident logged',        sub: `ID ${incident.id} created in CIMA`,               status: 'done' },
        { label: 'Commander notified',     sub: 'Email alert sent to Incident Commander',          status: 'done' },
        { label: 'CIMA agent chat (SIAP)', sub: 'Complete the Situation Report with the AI agent', status: 'current' },
        { label: 'Response team assembled',sub: 'Teams meeting created, team notified',             status: 'pending' },
      ]
    : [
        { label: 'Incident logged',        sub: `ID ${incident.id} created in CIMA`,               status: 'done' },
        { label: 'Response team assembled',sub: 'Teams meeting created, team notified',             status: 'pending' },
      ];
  const doneCount = steps.filter((s) => s.status === 'done').length;

  return (
    <div className="confirm-page">
      <div className="confirm-card">

        {/* ── Header ── */}
        <div className="confirm-head">
          <span className="confirm-check" aria-hidden>
            <CheckMark />
          </span>
          <span className="confirm-eyebrow">Your report has been logged and routed.</span>
        </div>
        <h1 className="confirm-title">Incident Reported</h1>
        <p className="confirm-sub">
          The Incident Commander has been notified and will respond shortly.
        </p>

        <div className="confirm-rule" />

        {/* ── Incident identity ── */}
        <div className="confirm-identity">
          <div>
            <div className="confirm-identity-title">{incident.title}</div>
            <div className="confirm-chips">
              <span
                className="severity-badge"
                style={{ color: colour, background: `${colour}18`, border: `1px solid ${colour}30` }}
              >
                {incident.severity}
              </span>
              <span
                className="status-badge"
                style={{ color: 'var(--accent-red)', background: 'color-mix(in srgb, var(--accent-red) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--accent-red) 24%, transparent)' }}
              >
                OPEN
              </span>
            </div>
          </div>
          <div className="confirm-idcol">
            <div className="confirm-idlabel">Incident ID</div>
            <div className="confirm-idnum">{incident.id}</div>
          </div>
        </div>

        {/* ── Meta ── */}
        <div className="confirm-meta">
          <div>
            <div className="confirm-metalabel">Location</div>
            <div className="confirm-metavalue">{incident.location}</div>
          </div>
          <div>
            <div className="confirm-metalabel">Occurred</div>
            <div className="confirm-metavalue">{occurredAt}</div>
          </div>
        </div>

        <div className="confirm-rule" />

        {/* ── Response pipeline ── */}
        <div className="confirm-pipe-head">
          <span className="confirm-pipe-title">Response pipeline</span>
          <span className="confirm-counter">{doneCount} / {steps.length}</span>
        </div>
        <div className="confirm-pipe">
          {steps.map((step) => (
            <div className={`confirm-node ${step.status}`} key={step.label}>
              <span className="confirm-node-marker" aria-hidden>
                {step.status === 'done' && <CheckMark />}
              </span>
              <div>
                <div className="confirm-node-label">{step.label}</div>
                <div className="confirm-node-sub">{step.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Actions ── */}
        <div className="confirm-actions">
          <button className="cancel-btn" onClick={onReportAnother}>
            Report another
          </button>
          {onStartChat ? (
            <button className="submit-btn" onClick={onStartChat}>
              Start SIAP →
            </button>
          ) : (
            <button className="submit-btn" onClick={onDashboard}>
              Go to Dashboard
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
